import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { get } from 'lodash';
import RoleService from 'services/role';
import PdButton from 'components/pd-button';
import queryString from 'query-string';
import { Col, Layout, Row, Input, message, Modal } from 'antd';

import './style.scss';

const { Content } = Layout;
const { Search } = Input;
const { confirm } = Modal;

export function Roles() {
  const location = useLocation();
  const [loadStatus, setLoadStatus] = useState(true);
  const [roleList, setRoleList] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const parsed = queryString.parse(location.search);
  const navigate = useNavigate();

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/roles',
      search: stringified,
    });
  }

  function convertPage(page) {
    let str = page.split('_');
    str = str.map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1);
    });
    return str.join(' ');
  }

  async function fetchRoleList(page = 1, q = '', total = 10) {
    setLoadStatus(true);
    try {
      const { data } = await RoleService.getRoles(page, q, total);
      const roleData = data.data;
      const metadata = get(data, 'metadata', { next_cursor: -1 });

      const mappedRole = roleData.map((role) => {
        const permi = get(role, 'permission', []).map((permi) => {
          permi.page = convertPage(permi.page);
          if (permi.page === 'Users') {
            permi.page = 'Customers';
          }
          if (permi.page === 'Product Collections') {
            permi.page = 'Collections';
          }
          return permi;
        });
        role.permission = permi;
        return role;
      });
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setRoleList(mappedRole);
    } finally {
      setLoadStatus(false);
    }
  }

  async function deleteBrand(id) {
    try {
      if (id) {
        await RoleService.deleteRole(id);
        message.success('Successfully deactive role');
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      fetchRoleList();
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure hide this role?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteBrand(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await RoleService.restoreRole(id);
        message.success('Successfully restored role');
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      fetchRoleList();
    }
  }

  const roleStatus = (row) => (
    <div className="status-indicator">
      <span className={row.status !== 'inactive' ? 'active' : 'hidden'}>
        {displayStatus(row.status)}
      </span>
    </div>
  );

  const rolePages = (row) => (
    <div className="pd-cms-role--pages">
      {row.permission.slice(0, 3).map((permi, i) => (
        <div key={i} className="pages">
          {permi.page}
        </div>
      ))}
      {row.permission.length > 3 && (
        <div className="more">+{row.permission.length - 3} more pages</div>
      )}
    </div>
  );

  const buttonAction = (row) => (
    <div className="button-table">
      <Row>
        <Col span={12} className="button">
          {row.status === 'active' ? (
            <PdButton
              type="danger"
              outline
              full
              style={{ height: 32 }}
              onClick={() => handleHide(row.id)}>
              Deactivate
            </PdButton>
          ) : (
            <PdButton
              type="primary"
              outline
              full
              style={{ height: 32 }}
              onClick={() => handleRestore(row.id)}>
              Reactivate
            </PdButton>
          )}
        </Col>
        <Col span={12} className="button">
          <Link
            to={{
              pathname: '/roles/edit',
              search: `id=${row.id}`,
            }}>
            <PdButton type="primary" full style={{ height: 32 }}>
              Edit
            </PdButton>
          </Link>
        </Col>
      </Row>
    </div>
  );

  const title = (row) => <span className="table-title--data">{row.title}</span>;

  const columns = [
    {
      name: 'Title',
      cell: title,
      width: '20%',
    },
    {
      name: 'Page Access',
      cell: rolePages,
      width: '40%',
    },
    {
      name: 'Status',
      cell: roleStatus,
      width: '12%',
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function searchOnClick(val) {
    if (val === '') {
      delete parsed['q'];
    } else {
      parsed['q'] = val;
    }
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/roles',
      search: stringified,
    });
  }

  useEffect(() => {
    if (parsed['q']) {
      setSearchValue(parsed['q']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/roles',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    fetchRoleList(parsed['page'], parsed['q']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed['q'], parsed['page']]);

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  return (
    <Layout className="pd-cms-roles main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Roles</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col xs={19}>
                <Search
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  enterButton
                  className="search-product"
                  placeholder="Search Role"
                  onSearch={searchOnClick}
                />
              </Col>

              <Col xs={{ span: 3 }}>
                <Link
                  to={{
                    pathname: `/roles/create`,
                  }}>
                  <PdButton
                    type="primary"
                    full
                    outline
                    style={{ height: 32, width: 176 }}>
                    Add New Role
                  </PdButton>
                </Link>
              </Col>

              {!loadStatus && (
                <>
                  <Col xs={24}>
                    <DataTable
                      columns={columns}
                      data={roleList}
                      progressPending={loadStatus}
                      noHeader
                      highlightOnHover
                      responsive={false}
                      striped
                    />
                  </Col>

                  <Col span={8} offset={8}>
                    <div className="center-button">
                      {page !== '1' && (
                        <PdButton
                          type="primary"
                          outline
                          style={{ height: 32 }}
                          onClick={() => changePage(Number(page) - 1)}>
                          Previous
                        </PdButton>
                      )}
                      {nextPage !== -1 && (
                        <PdButton
                          type="primary"
                          outline
                          style={{ height: 32 }}
                          onClick={() => changePage(Number(page) + 1)}>
                          Next
                        </PdButton>
                      )}
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { Roles };
